import {
  ArrowDownAZ,
  Bot,
  CalendarPlus,
  ChevronDown,
  Clock,
  CopyPlus,
  LayoutGrid,
  List,
  MessageSquare,
  MoreHorizontal,
  Network,
  Pencil,
  Plus,
  Star,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useOrgNavigate } from '@/hooks/use-org-navigate';

import { Modal } from '@/components/modal';
import { useNotification } from '@/components/notifications';
import { Pagination } from '@/components/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SearchInput } from '@/components/ui/search-input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ROUTES } from '@/constants/routes';
import {
  agentsQueryKey,
  useListAgents,
} from '@/hooks/queries/agents/use-list-agents';
import { primaryAgentQueryKey } from '@/hooks/queries/agents/use-primary-agent';
import {
  AgentsTable,
  agentWorkflowScopeCount,
} from '@/pages/agents/components/agents-table';
import {
  deleteAgent,
  duplicateAgent,
  updateAgent,
} from '@/services/agents/config';
import { useOrgStore } from '@/stores/org-store';
import type { Agent, AgentSortField } from '@/types/agent-types';

const ITEMS_PER_PAGE = 10;
const VIEW_MODE_STORAGE_KEY = 'agents-list-view-mode';

type ViewMode = 'grid' | 'list';
type SortDirection = 'asc' | 'desc';

const SORT_OPTIONS: {
  field: AgentSortField;
  label: string;
  icon: React.ReactNode;
}[] = [
  { field: 'name', label: 'Name', icon: <ArrowDownAZ className='size-4' /> },
  {
    field: 'created_at',
    label: 'Date Created',
    icon: <CalendarPlus className='size-4' />,
  },
  {
    field: 'updated_at',
    label: 'Last Modified',
    icon: <Clock className='size-4' />,
  },
];

function sortValue(agent: Agent, field: AgentSortField): string | number {
  switch (field) {
    case 'name':
      return agent.name.toLowerCase();
    case 'exposed_via_a2a':
      return agent.exposed_via_a2a ? 1 : 0;
    case 'model_name':
      return (agent.model_name || agent.model_provider || '').toLowerCase();
    case 'workflow_scope':
      return agentWorkflowScopeCount(agent);
    case 'created_at':
      return agent.created_at ? new Date(agent.created_at).getTime() : 0;
    case 'updated_at':
      return agent.updated_at ? new Date(agent.updated_at).getTime() : 0;
  }
}

export function AgentsListPage() {
  const navigate = useOrgNavigate();
  const notification = useNotification();
  const queryClient = useQueryClient();
  const currentOrg = useOrgStore((s) => s.currentOrg);

  const { data: agents = [], isLoading, error: loadError } = useListAgents();

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY) as ViewMode;
    return saved || 'list';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<AgentSortField>('updated_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  const [actionError, setActionError] = useState<string | null>(null);
  const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Per-agent guard so a slow duplicate doesn't let the user double-fire
  // the action on the same source agent (which would create two copies
  // with the same "Copy of …" name and confuse the operator).
  const [duplicatingAgentId, setDuplicatingAgentId] = useState<string | null>(
    null,
  );
  const [settingPrimaryAgentId, setSettingPrimaryAgentId] = useState<
    string | null
  >(null);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  };

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleSort = (field: AgentSortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'name' ? 'asc' : 'desc');
    }
    setCurrentPage(1);
  };

  const setAgentsCache = (updater: (prev: Agent[]) => Agent[]) => {
    queryClient.setQueryData<Agent[]>(
      agentsQueryKey(currentOrg?.orgid),
      (prev) => updater(prev ?? []),
    );
  };

  const filteredAndSortedAgents = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = query
      ? agents.filter(
          (agent) =>
            agent.name.toLowerCase().includes(query) ||
            (agent.description &&
              agent.description.toLowerCase().includes(query)),
        )
      : agents;

    return [...filtered].sort((a, b) => {
      const aValue = sortValue(a, sortField);
      const bValue = sortValue(b, sortField);
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [agents, searchQuery, sortField, sortDirection]);

  const totalPages = Math.ceil(filteredAndSortedAgents.length / ITEMS_PER_PAGE);

  const paginatedAgents = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAndSortedAgents.slice(
      startIndex,
      startIndex + ITEMS_PER_PAGE,
    );
  }, [filteredAndSortedAgents, currentPage]);

  const handleConfigure = (agent: Agent) =>
    navigate(ROUTES.AGENTS.CONFIG.replace(':agentId', agent.id));

  const handleChat = (agent: Agent) =>
    navigate(ROUTES.AGENTS.CHAT.replace(':agentId', agent.id));

  const handleDeleteConfirm = async () => {
    if (!currentOrg?.orgid || !agentToDelete) return;
    setIsDeleting(true);
    try {
      await deleteAgent(currentOrg.orgid, agentToDelete.id);
      setAgentsCache((prev) => prev.filter((a) => a.id !== agentToDelete.id));
      setAgentToDelete(null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to delete agent');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDuplicate = async (source: Agent) => {
    if (!currentOrg?.orgid || duplicatingAgentId) return;
    setDuplicatingAgentId(source.id);
    setActionError(null);
    try {
      // Pass the source through so the service skips the redundant GET --
      // the list row is the freshest record we have (the only thing that
      // could be staler is an in-flight unsaved edit on the config page,
      // and that hasn't been persisted yet so it isn't ours to copy).
      const created = await duplicateAgent(currentOrg.orgid, source.id, {
        sourceAgent: source,
      });
      if (!created?.agent) {
        throw new Error('Duplicate request returned no agent');
      }
      // Optimistically insert the new agent at the top of the list so
      // the user sees the result even if they navigate back from the
      // config page before a fresh listAgents() lands.
      setAgentsCache((prev) => [created.agent, ...prev]);
      notification.success(`Duplicated "${source.name}"`);
      // Jump into the config page so the operator can immediately tweak
      // the name, bearer label, or any field they wanted to differ on
      // the copy. The new agent's bearer was just minted server-side
      // and is visible on its config page.
      navigate(ROUTES.AGENTS.CONFIG.replace(':agentId', created.agent.id));
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to duplicate agent';
      setActionError(message);
      notification.error(`Failed to duplicate "${source.name}": ${message}`);
    } finally {
      setDuplicatingAgentId(null);
    }
  };

  const handleSetPrimary = async (agent: Agent) => {
    if (!currentOrg?.orgid || settingPrimaryAgentId || agent.is_org_primary) {
      return;
    }
    setSettingPrimaryAgentId(agent.id);
    setActionError(null);
    try {
      await updateAgent(currentOrg.orgid, agent.id, { is_org_primary: true });
      setAgentsCache((prev) =>
        prev.map((a) => ({ ...a, is_org_primary: a.id === agent.id })),
      );
      queryClient.invalidateQueries({
        queryKey: primaryAgentQueryKey(currentOrg.orgid),
      });
      notification.success(`"${agent.name}" is now the org's primary agent`);
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to set primary agent';
      Sentry.captureException(e, {
        extra: { context: 'agents-list-set-primary', agentId: agent.id },
      });
      setActionError(message);
      notification.error(
        `Failed to set "${agent.name}" as primary: ${message}`,
      );
    } finally {
      setSettingPrimaryAgentId(null);
    }
  };

  const error = actionError || (loadError ? loadError.message : null);
  const sortButtonLabel =
    SORT_OPTIONS.find((o) => o.field === sortField)?.label ?? 'Sort';
  const hasAgents = agents.length > 0;
  const hasResults = filteredAndSortedAgents.length > 0;

  return (
    <div className='w-full p-6'>
      {/* Header */}
      <div className='flex items-start justify-between mb-6'>
        <div className='flex items-start gap-3'>
          <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary'>
            <Bot className='h-5 w-5' />
          </div>
          <div>
            <h1 className='text-2xl font-bold tracking-tight'>Agents</h1>
            <p className='text-sm text-muted-foreground mt-1 max-w-2xl'>
              A2A-callable agents for this organization. External peers (like
              upstream orchestrators) call into these agents over the A2A
              protocol; chat consumers use the per-agent chat surface.
            </p>
          </div>
        </div>
        <Button onClick={() => navigate(ROUTES.AGENTS.NEW)}>
          <Plus className='mr-2 h-4 w-4' />
          New Agent
        </Button>
      </div>

      {/* Controls: search, sort, view toggle */}
      {hasAgents && (
        <div className='flex flex-col xl:flex-row xl:items-center gap-3 mb-6'>
          <div className='w-full xl:w-64'>
            <SearchInput
              value={searchQuery}
              onChange={handleSearchQueryChange}
              placeholder='Search agents'
              aria-label='Search agents by name or description'
            />
          </div>
          <div className='flex items-center gap-3 xl:flex-1'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='outline' className='flex items-center gap-2'>
                  <Clock className='size-4' />
                  {sortButtonLabel}
                  <span className='text-xs text-muted-foreground'>
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                  <ChevronDown className='size-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='start'>
                {SORT_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.field}
                    onClick={() => handleSort(option.field)}
                    className='flex items-center gap-2'
                  >
                    {option.icon}
                    {option.label}
                    {sortField === option.field && (
                      <span className='ml-auto text-xs text-muted-foreground'>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <div className='ml-auto flex items-center gap-2'>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={viewMode === 'grid' ? 'outline' : 'ghost'}
                    size='sm'
                    className='h-8 w-8 p-0'
                    onClick={() => handleViewModeChange('grid')}
                    aria-label='Grid View'
                  >
                    <LayoutGrid className='size-4' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>Grid View</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={viewMode === 'list' ? 'outline' : 'ghost'}
                    size='sm'
                    className='h-8 w-8 p-0'
                    onClick={() => handleViewModeChange('list')}
                    aria-label='Table View'
                  >
                    <List className='size-4' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>Table View</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className='rounded-lg border border-destructive bg-destructive/10 p-4 mb-6'>
          <p className='text-sm text-destructive'>{error}</p>
        </div>
      )}

      {/* Loading skeleton */}
      {isLoading && (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3'>
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className='h-44 rounded-lg border border-border bg-muted/50 animate-pulse'
            />
          ))}
        </div>
      )}

      {/* Agent list */}
      {!isLoading && hasResults && (
        <>
          {viewMode === 'grid' ? (
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3'>
              {paginatedAgents.map((agent) => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  isDuplicating={duplicatingAgentId === agent.id}
                  isSettingPrimary={settingPrimaryAgentId === agent.id}
                  onConfigure={() => handleConfigure(agent)}
                  onChat={() => handleChat(agent)}
                  onDuplicate={() => handleDuplicate(agent)}
                  onSetPrimary={() => handleSetPrimary(agent)}
                  onDelete={() => setAgentToDelete(agent)}
                />
              ))}
            </div>
          ) : (
            <AgentsTable
              agents={paginatedAgents}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              duplicatingAgentId={duplicatingAgentId}
              settingPrimaryAgentId={settingPrimaryAgentId}
              onConfigure={handleConfigure}
              onChat={handleChat}
              onDuplicate={handleDuplicate}
              onSetPrimary={handleSetPrimary}
              onDelete={setAgentToDelete}
            />
          )}
          {totalPages > 1 && (
            <div className='mt-4'>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      {/* No search results */}
      {!isLoading && hasAgents && !hasResults && (
        <div className='rounded-lg border border-dashed border-border bg-muted/30 px-6 py-16 text-center'>
          <p className='text-sm text-muted-foreground'>
            No agents match “{searchQuery}”.
          </p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && !hasAgents && (
        <div className='rounded-lg border border-dashed border-border bg-muted/30 px-6 py-16'>
          <div className='flex flex-col items-center justify-center text-center max-w-md mx-auto'>
            <div className='flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary mb-5'>
              <Network className='h-8 w-8' />
            </div>
            <h3 className='text-lg font-semibold tracking-tight'>
              No agents yet
            </h3>
            <p className='text-sm text-muted-foreground mt-1.5 mb-6'>
              Create your first A2A agent to expose Capitol workflows to
              external orchestrators or give your team a chat surface over your
              data and tools.
            </p>
            <Button onClick={() => navigate(ROUTES.AGENTS.NEW)}>
              <Plus className='mr-2 h-4 w-4' />
              Create Agent
            </Button>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {agentToDelete && (
        <Modal
          isDialogOpen={!!agentToDelete}
          setIsDialogOpen={(open) => {
            if (!open) setAgentToDelete(null);
          }}
          title={`Delete "${agentToDelete.name}"?`}
        >
          <div className='space-y-4'>
            <p className='text-sm text-muted-foreground'>
              This permanently deletes the agent and revokes its A2A bearer
              token. External peers calling this agent will start receiving 401s
              immediately.
            </p>
            <div className='flex justify-end gap-2'>
              <Button
                variant='outline'
                onClick={() => setAgentToDelete(null)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant='destructive'
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

interface AgentCardProps {
  agent: Agent;
  isDuplicating: boolean;
  isSettingPrimary: boolean;
  onConfigure: () => void;
  onChat: () => void;
  onDuplicate: () => void;
  onSetPrimary: () => void;
  onDelete: () => void;
}

function timeAgo(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Edited just now';
  if (diffMins < 60) return `Edited ${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `Edited ${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `Edited ${diffDays}d ago`;
}

function AgentCard({
  agent,
  isDuplicating,
  isSettingPrimary,
  onConfigure,
  onChat,
  onDuplicate,
  onSetPrimary,
  onDelete,
}: AgentCardProps) {
  const handleCardClick = () => {
    onConfigure();
  };

  const handleAction = (cb: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    cb();
  };

  const labelCount = agent.workflow_allowlist_labels?.length ?? 0;
  const allowCount = agent.workflow_allowlist?.length ?? 0;
  const hasScope = labelCount > 0 || allowCount > 0;

  return (
    <div
      onClick={handleCardClick}
      className='group relative flex flex-col gap-3 rounded-lg border border-border bg-card p-4 cursor-pointer transition-all hover:border-primary/50 hover:shadow-md'
    >
      {/* Top row: icon + status + menu */}
      <div className='flex items-start justify-between'>
        <div className='flex items-center gap-2'>
          <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary'>
            <Bot className='h-5 w-5' />
          </div>
          {agent.is_org_primary && (
            <Badge
              variant='secondary'
              className='bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400'
              title="The org's primary chat agent"
            >
              <Star className='mr-1 size-3' />
              Primary
            </Badge>
          )}
          {agent.exposed_via_a2a ? (
            <Badge
              variant='secondary'
              className='bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400'
              title='A2A protocol surface enabled'
            >
              <span className='mr-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500' />
              A2A live
            </Badge>
          ) : (
            <Badge
              variant='secondary'
              className='text-muted-foreground'
              title='A2A protocol surface disabled'
            >
              A2A off
            </Badge>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button
              variant='ghost'
              size='icon'
              className='size-8 opacity-0 group-hover:opacity-100 transition-opacity'
            >
              <MoreHorizontal className='size-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem onClick={handleAction(onChat)}>
              <MessageSquare className='size-4 mr-2' />
              Chat
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleAction(onConfigure)}>
              <Pencil className='size-4 mr-2' />
              Configure
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleAction(onDuplicate)}
              disabled={isDuplicating}
            >
              <CopyPlus className='size-4 mr-2' />
              {isDuplicating ? 'Duplicating…' : 'Duplicate'}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleAction(onSetPrimary)}
              disabled={isSettingPrimary || agent.is_org_primary}
            >
              <Star className='size-4 mr-2' />
              {agent.is_org_primary
                ? 'Primary agent'
                : isSettingPrimary
                  ? 'Setting as primary…'
                  : 'Set as primary'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleAction(onDelete)}
              className='text-destructive focus:text-destructive'
            >
              <Trash2 className='size-4 mr-2' />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Name + description */}
      <div className='min-h-[3.5rem]'>
        <h3
          className='font-semibold text-sm text-foreground line-clamp-1'
          title={agent.name}
        >
          {agent.name}
        </h3>
        <p
          className='text-xs text-muted-foreground mt-1 line-clamp-2'
          title={agent.description || 'No description'}
        >
          {agent.description || 'No description'}
        </p>
      </div>

      {/* Model + scope chips */}
      <div className='flex flex-wrap items-center gap-1.5'>
        <Badge variant='outline' className='font-mono text-[10px]'>
          {agent.model_name || agent.model_provider || 'no model'}
        </Badge>
        {hasScope && (
          <Badge variant='outline' className='text-[10px]'>
            {labelCount > 0
              ? `${labelCount} label${labelCount === 1 ? '' : 's'}`
              : `${allowCount} workflow${allowCount === 1 ? '' : 's'}`}
          </Badge>
        )}
      </div>

      {/* Footer */}
      <div className='flex items-center justify-between mt-auto pt-1'>
        <span className='text-xs text-muted-foreground'>
          {timeAgo(agent.updated_at) || 'New'}
        </span>
      </div>
    </div>
  );
}
